import { useCallback, useRef, useState } from 'react'

import { useAuthStateChange } from '@/modules/auth/hooks/useAuthStateChange'
import {
  type Channel,
  type ChannelMixin,
  type Consumer,
  createConsumer,
  disconnectConsumer,
  subscribe,
  unsubscribe,
} from '@/services/lukita/websocket'
import { WebsocketContext } from './websocketContext'

interface WebsocketProviderProps {
  children: React.ReactNode
}

export const WebsocketProvider = ({ children }: WebsocketProviderProps) => {
  const [consumer, setConsumer] = useState<Consumer | null>(null)
  const consumerRef = useRef<Consumer | null>(null)

  const handleConnect = () =>
    createConsumer().then((next) => {
      consumerRef.current = next
      setConsumer(next)
    })

  const handleDisconnect = () => {
    disconnectConsumer(consumerRef.current)
    consumerRef.current = null
    setConsumer(null)
  }

  const handleSubscribe = useCallback(
    (channel: Channel, mixin: Partial<ChannelMixin>) =>
      subscribe(consumer, channel, mixin),
    [consumer],
  )

  useAuthStateChange({
    onInitialSession: handleConnect,
    onSignedIn: handleConnect,
    onTokenRefreshed: handleConnect,
    onSignedOut: handleDisconnect,
  })

  return (
    <WebsocketContext.Provider
      value={{
        isReady: consumer !== null,
        consumer,
        subscribe: handleSubscribe,
        unsubscribe,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  )
}
